import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { guestPlayerService, type LessonInfo } from '@/services/guestPlayerService';
import styles from './GuestJoin.module.css';

interface RunInfo {
  id: string;
  title: string;
  run_code: string;
  status: string;
}

export function GuestJoin() {
  const { runCode: urlRunCode } = useParams<{ runCode: string }>();
  const navigate = useNavigate();

  const [lessonInfo, setLessonInfo] = useState<LessonInfo | null>(null);
  const [runInfo, setRunInfo] = useState<RunInfo | null>(null);
  const [waitingForLesson, setWaitingForLesson] = useState(false);
  const [runCode, setRunCode] = useState(urlRunCode || '');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (urlRunCode) {
      loadLessonInfo(urlRunCode);
    } else {
      setIsLoading(false);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [urlRunCode]);

  const loadLessonInfo = async (code?: string) => {
    const codeToUse = code || runCode;
    if (!codeToUse) return;

    setIsLoading(true);
    setError(null);
    setHasSearched(true);

    const result = await guestPlayerService.getLessonInfo(codeToUse);

    if (result.success && result.data) {
      if (result.data.lesson) {
        setLessonInfo(result.data.lesson);
        setWaitingForLesson(false);
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      } else {
        // Run found but no active lesson yet
        setLessonInfo(null);
        setRunInfo((result.data as any).run ?? null);
        setWaitingForLesson(true);
        // Poll every 5s for an active lesson
        if (!pollRef.current) {
          pollRef.current = setInterval(() => pollForLesson(codeToUse), 5000);
        }
      }
      const storedName = guestPlayerService.getStoredDisplayName();
      if (storedName) {
        setDisplayName(storedName);
      }
    } else {
      setError(result.error || 'Lesson not found');
      setLessonInfo(null);
      setRunInfo(null);
      setWaitingForLesson(false);
    }

    setIsLoading(false);
  };

  const pollForLesson = async (code: string) => {
    const result = await guestPlayerService.getLessonInfo(code);
    if (result.success && result.data?.lesson) {
      setLessonInfo(result.data.lesson);
      setWaitingForLesson(false);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
  };

  const handleFindLesson = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!runCode.trim()) {
      setError('Please enter a join code');
      return;
    }
    await loadLessonInfo();
  };

  const handleJoinLesson = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!runCode || !displayName.trim()) {
      setError('Please enter your name');
      return;
    }

    setIsJoining(true);
    setError(null);

    const result = await guestPlayerService.joinLesson(runCode, displayName.trim());

    if (result.success) {
      navigate(`/play/${runCode}`);
    } else {
      setError(result.error || 'Failed to join lesson');
      setIsJoining(false);
    }
  };

  // ── Loading ──
  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.skeleton}>
            <div className={`${styles.skeletonLine} ${styles.skeletonLineWide}`} />
            <div className={`${styles.skeletonLine} ${styles.skeletonLineNarrow}`} />
            <div className={`${styles.skeletonLine} ${styles.skeletonLineFull}`} />
            <div className={`${styles.skeletonLine} ${styles.skeletonLineFull}`} />
          </div>
        </div>
      </div>
    );
  }

  // ── Waiting for lesson to start ──
  if (waitingForLesson && !lessonInfo && !isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.iconWrap}>
            <svg className={styles.iconSvg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className={styles.title}>{runInfo?.title || 'Waiting for Lesson'}</h1>
          <p className={styles.subtitle}>
            The instructor hasn't started a lesson yet. Please wait — you'll be able to join as soon as it begins.
          </p>

          <p style={{ textAlign: 'center', marginTop: '1rem' }}>
            <span className={styles.lessonCodeValue}>{runCode}</span>
          </p>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginTop: '1.5rem', opacity: 0.6 }}>
            <svg style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} fill="none" viewBox="0 0 24 24">
              <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span style={{ fontSize: '0.875rem' }}>Checking for active lesson...</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Lesson code entry (no lesson found yet) ──
  if (!lessonInfo && !isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.iconWrap}>
            <svg className={styles.iconSvg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m-9 5a9 9 0 1118 0z" />
            </svg>
          </div>
          <h1 className={styles.title}>Join a Lesson</h1>
          <p className={styles.subtitle}>Enter the join code to get started</p>

          <form onSubmit={handleFindLesson} className={styles.form}>
            <div className={styles.formGroup}>
              <label htmlFor="runCode">Join Code</label>
              <input
                type="text"
                id="runCode"
                value={runCode}
                onChange={(e) => setRunCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                className={styles.inputCode}
                maxLength={10}
                required
              />
            </div>

            {error && hasSearched && (
              <div className={styles.errorBox}>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!runCode.trim()}
              className={styles.btnPrimary}
            >
              Find Lesson
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Lesson found — show join form ──
  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Lesson Info */}
        <div className={styles.iconWrap}>
          <svg className={styles.iconSvg} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h8m-9 5a9 9 0 1118 0z" />
          </svg>
        </div>

        <h1 className={styles.lessonName}>{lessonInfo?.name}</h1>

        <p className={styles.lessonCode}>
          Join Code: <span className={styles.lessonCodeValue}>{runCode}</span>
        </p>

        {lessonInfo?.description && (
          <p className={styles.lessonDescription}>{lessonInfo.description}</p>
        )}

        {/* Join Form */}
        <form onSubmit={handleJoinLesson} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="displayName">Enter your name to join</label>
            <input
              type="text"
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your display name"
              className={styles.input}
              maxLength={100}
              required
              disabled={isJoining}
            />
          </div>

          {error && (
            <div className={styles.errorBox}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={isJoining || !displayName.trim()}
            className={styles.btnPrimary}
          >
            {isJoining ? (
              <>
                <svg className={styles.spinner} fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Joining...
              </>
            ) : (
              'Join Lesson'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className={styles.footer}>
          <p className={styles.footerText}>
            Ready to learn? Join now and participate in real-time activities!
          </p>
        </div>
      </div>
    </div>
  );
}
